/**
 * Device Property Definitions
 *
 * Defines available properties for each device type, their default values,
 * and helpers for property inclusion/exclusion in scene device targets.
 */

import type { DevicesDevice, DevicesDeviceState } from '@smarthome/api';

// Property category
export type PropertyCategory = 'common' | 'advanced';

// Property definition
export interface PropertyDef {
  /** Property path (e.g., 'tv.on', 'light.brightness') */
  path: string;
  /** Display label */
  label: string;
  /** Category for grouping */
  category: PropertyCategory;
  /** Type of control to render */
  controlType: 'toggle' | 'slider' | 'select';
  /** Function to get default value (can use current device state) */
  getDefaultValue: (device: DevicesDevice) => unknown;
  /** Function to format value for display in summary */
  formatSummary: (value: unknown) => string;
  /** Whether this is the primary property (auto-included when device is added) */
  isPrimary?: boolean;
  /** Slider config (if controlType is 'slider') */
  sliderConfig?: {
    min: number;
    max: number;
    formatValue?: (v: number) => string;
    hint?: string;
  };
  /** Select options getter (if controlType is 'select') */
  getSelectOptions?: (device: DevicesDevice) => Array<{ value: string; label: string }>;
  /** Capability check - only show if device has this capability */
  requiresCapability?: (capabilities: DevicesDevice['capabilities']) => boolean;
}

// Device type property definitions
const SWITCH_PROPERTIES: PropertyDef[] = [
  {
    path: 'switch.on',
    label: 'Power',
    category: 'common',
    controlType: 'toggle',
    getDefaultValue: (device) => device.state?.switch?.on ?? true,
    formatSummary: (v) => (v ? 'On' : 'Off'),
    isPrimary: true,
    // Only show Power toggle for non-multi-position switches
    requiresCapability: (cap) => !(cap?.switch?.positions && cap.switch.positions.length > 0),
  },
  {
    path: 'switch.position',
    label: 'Position',
    category: 'common',
    controlType: 'select',
    getDefaultValue: (device) => {
      const positions = device.capabilities?.switch?.positions ?? [];
      return device.state?.switch?.position ?? positions[0]?.id ?? '';
    },
    formatSummary: (v) => String(v) || 'None',
    isPrimary: true,
    getSelectOptions: (device) =>
      (device.capabilities?.switch?.positions ?? []).map((p: { id?: string; label?: string }) => ({
        value: p.id ?? '',
        label: p.label ?? p.id ?? '',
      })),
    requiresCapability: (cap) => (cap?.switch?.positions?.length ?? 0) > 0,
  },
];

const LIGHT_PROPERTIES: PropertyDef[] = [
  {
    path: 'light.on',
    label: 'Power',
    category: 'common',
    controlType: 'toggle',
    getDefaultValue: (device) => device.state?.light?.on ?? true,
    formatSummary: (v) => (v ? 'On' : 'Off'),
    isPrimary: true,
  },
  {
    path: 'light.brightness',
    label: 'Brightness',
    category: 'common',
    controlType: 'slider',
    getDefaultValue: (device) => device.state?.light?.brightness ?? 255,
    formatSummary: (v) => `${Math.round((Number(v) / 255) * 100)}%`,
    sliderConfig: {
      min: 0,
      max: 255,
      formatValue: (v) => `${Math.round((v / 255) * 100)}%`,
    },
    requiresCapability: (cap) => cap?.light?.brightness ?? false,
  },
  {
    path: 'light.color',
    label: 'Color',
    category: 'advanced',
    controlType: 'select', // Will be handled specially as color picker
    getDefaultValue: (device) => device.state?.light?.color ?? { red: 255, green: 244, blue: 229 },
    formatSummary: () => 'Custom',
    requiresCapability: (cap) => cap?.light?.color ?? false,
  },
  {
    path: 'light.colorTemperaturePresetId',
    label: 'Color Temperature',
    category: 'advanced',
    controlType: 'select',
    getDefaultValue: (device) => {
      const presets = device.capabilities?.light?.colorTemperaturePresets ?? [];
      return device.state?.light?.colorTemperaturePresetId ?? presets[0]?.id ?? '';
    },
    formatSummary: (v) => String(v),
    getSelectOptions: (device) => {
      const presets = device.capabilities?.light?.colorTemperaturePresets ?? [];
      return presets.map((preset) => ({ value: preset.id ?? '', label: preset.label ?? '' }));
    },
    requiresCapability: (cap) => {
      const presets = cap?.light?.colorTemperaturePresets ?? [];
      return (cap?.light?.colorTemperature ?? false) && presets.length > 0;
    },
  },
];

const WINDOW_COVERING_PROPERTIES: PropertyDef[] = [
  {
    path: 'windowCovering.closedPercentage',
    label: 'Position',
    category: 'common',
    controlType: 'slider',
    getDefaultValue: (device) => device.state?.windowCovering?.closedPercentage ?? 0,
    formatSummary: (v) => `${v}% closed`,
    isPrimary: true,
    sliderConfig: {
      min: 0,
      max: 100,
      formatValue: (v) => `${v}% closed`,
    },
    requiresCapability: (cap) => cap?.windowCovering?.position ?? true, // Default to showing position
  },
];

const FAN_PROPERTIES: PropertyDef[] = [
  {
    path: 'fan.on',
    label: 'Power',
    category: 'common',
    controlType: 'toggle',
    getDefaultValue: (device) => device.state?.fan?.on ?? true,
    formatSummary: (v) => (v ? 'On' : 'Off'),
    isPrimary: true,
  },
  {
    path: 'fan.intensity',
    label: 'Intensity',
    category: 'common',
    controlType: 'slider',
    getDefaultValue: (device) => device.state?.fan?.intensity ?? 128,
    formatSummary: (v) => `${Math.round((Number(v) / 255) * 100)}%`,
    sliderConfig: {
      min: 0,
      max: 255,
      formatValue: (v) => `${Math.round((v / 255) * 100)}%`,
    },
    requiresCapability: (cap) => cap?.fan?.speed ?? false,
  },
  {
    path: 'fan.swivel',
    label: 'Swivel',
    category: 'advanced',
    controlType: 'toggle',
    getDefaultValue: (device) => device.state?.fan?.swivel ?? false,
    formatSummary: (v) => (v ? 'On' : 'Off'),
    requiresCapability: (cap) => cap?.fan?.swivel ?? false,
  },
];

const SPEAKER_PROPERTIES: PropertyDef[] = [
  {
    path: 'speaker.on',
    label: 'Power',
    category: 'common',
    controlType: 'toggle',
    getDefaultValue: (device) => device.state?.speaker?.on ?? true,
    formatSummary: (v) => (v ? 'On' : 'Off'),
    isPrimary: true,
  },
  {
    path: 'speaker.volume',
    label: 'Volume',
    category: 'common',
    controlType: 'slider',
    getDefaultValue: (device) => device.state?.speaker?.volume ?? 50,
    formatSummary: (v) => `${Math.round((Number(v) / 255) * 100)}%`,
    sliderConfig: {
      min: 0,
      max: 255,
      formatValue: (v) => `${Math.round((v / 255) * 100)}%`,
    },
    requiresCapability: (cap) => cap?.speaker?.volumeIncrement ?? false,
  },
  {
    path: 'speaker.source',
    label: 'Source',
    category: 'advanced',
    controlType: 'select',
    getDefaultValue: (device) => device.state?.speaker?.source ?? '',
    formatSummary: (v) => (v ? String(v) : 'None'),
    getSelectOptions: (device) =>
      (device.capabilities?.speaker?.sources ?? []).map((s: { id?: string; label?: string }) => ({
        value: s.id ?? '',
        label: s.label ?? s.id ?? '',
      })),
    requiresCapability: (cap) => (cap?.speaker?.sources?.length ?? 0) > 0,
  },
  {
    path: 'speaker.playState',
    label: 'Play State',
    category: 'advanced',
    controlType: 'select',
    getDefaultValue: () => 'playing',
    formatSummary: (v) => String(v),
    getSelectOptions: () => [
      { value: 'playing', label: 'Playing' },
      { value: 'paused', label: 'Paused' },
      { value: 'stopped', label: 'Stopped' },
    ],
    requiresCapability: (cap) => cap?.speaker?.playControl ?? false,
  },
];

const TV_PROPERTIES: PropertyDef[] = [
  {
    path: 'tv.on',
    label: 'Power',
    category: 'common',
    controlType: 'toggle',
    getDefaultValue: (device) => device.state?.tv?.on ?? true,
    formatSummary: (v) => (v ? 'On' : 'Off'),
    isPrimary: true,
  },
  {
    path: 'tv.volume',
    label: 'Volume',
    category: 'common',
    controlType: 'slider',
    getDefaultValue: (device) => device.state?.tv?.volume ?? 25,
    formatSummary: (v) => `${v}%`,
    sliderConfig: {
      min: 0,
      max: 100,
      formatValue: (v) => `${v}%`,
    },
    requiresCapability: (cap) => cap?.tv?.volumeIncrement ?? false,
  },
  {
    path: 'tv.muted',
    label: 'Muted',
    category: 'common',
    controlType: 'toggle',
    getDefaultValue: (device) => device.state?.tv?.muted ?? false,
    formatSummary: (v) => (v ? 'Yes' : 'No'),
    requiresCapability: (cap) => cap?.tv?.volumeIncrement ?? false,
  },
  {
    path: 'tv.source',
    label: 'Input Source',
    category: 'advanced',
    controlType: 'select',
    getDefaultValue: (device) => device.state?.tv?.source ?? '',
    formatSummary: (v) => (v ? String(v) : 'None'),
    getSelectOptions: (device) =>
      (device.capabilities?.tv?.inputSources ?? []).map((s: { id?: string; label?: string }) => ({
        value: s.id ?? '',
        label: s.label ?? s.id ?? '',
      })),
    requiresCapability: (cap) => (cap?.tv?.inputSources?.length ?? 0) > 0,
  },
  {
    path: 'tv.source',
    label: 'Channel',
    category: 'common',
    controlType: 'select',
    getDefaultValue: (device) => device.state?.tv?.source ?? '',
    formatSummary: (v) => (v ? String(v) : 'None'),
    getSelectOptions: (device) =>
      (device.capabilities?.tv?.channels ?? []).map(
        (c: { id?: string; name?: string; number?: number }) => ({
          value: c.id ?? '',
          label: c.number ? `${c.number}. ${c.name ?? c.id ?? ''}` : (c.name ?? c.id ?? ''),
        })
      ),
    requiresCapability: (cap) => (cap?.tv?.channels?.length ?? 0) > 0,
  },
];

// Map device types to their property definitions
const DEVICE_PROPERTIES: Record<string, PropertyDef[]> = {
  switch: SWITCH_PROPERTIES,
  light: LIGHT_PROPERTIES,
  window_covering: WINDOW_COVERING_PROPERTIES,
  fan: FAN_PROPERTIES,
  speaker: SPEAKER_PROPERTIES,
  tv: TV_PROPERTIES,
};

/**
 * Get all available property definitions for a device
 */
export function getPropertyDefsForDevice(device: DevicesDevice): PropertyDef[] {
  const type = device.type ?? 'switch';
  const allProps = DEVICE_PROPERTIES[type] ?? SWITCH_PROPERTIES;

  // Filter by capability requirements
  return allProps.filter((prop) => {
    if (!prop.requiresCapability) return true;
    return prop.requiresCapability(device.capabilities);
  });
}

/**
 * Get the primary property that should be auto-included when adding a device
 */
export function getPrimaryPropertyDef(device: DevicesDevice): PropertyDef | undefined {
  const props = getPropertyDefsForDevice(device);
  return props.find((p) => p.isPrimary);
}

/**
 * Get property value from state using path (e.g., 'tv.volume')
 */
export function getPropertyValue(state: DevicesDeviceState, path: string): unknown | undefined {
  const [type, prop] = path.split('.') as [keyof DevicesDeviceState, string];
  const typeState = state[type] as Record<string, unknown> | undefined;
  return typeState?.[prop];
}

/**
 * Set property value in state using path
 */
export function setPropertyValue(
  state: DevicesDeviceState,
  path: string,
  value: unknown
): DevicesDeviceState {
  const [type, prop] = path.split('.') as [keyof DevicesDeviceState, string];
  const typeState = state[type] as Record<string, unknown> | undefined;

  return {
    ...state,
    [type]: {
      ...typeState,
      [prop]: value,
    },
  };
}

/**
 * Remove a property from state
 */
export function removeProperty(state: DevicesDeviceState, path: string): DevicesDeviceState {
  const [type, prop] = path.split('.') as [keyof DevicesDeviceState, string];
  const typeState = { ...(state[type] as Record<string, unknown> | undefined) };

  if (typeState) {
    delete typeState[prop];
  }

  // If type object is now empty, remove it entirely
  if (!typeState || Object.keys(typeState).length === 0) {
    const newState = { ...state };
    delete newState[type];
    return newState;
  }

  return {
    ...state,
    [type]: typeState,
  };
}

/**
 * Check if a property is included in state
 */
export function isPropertyIncluded(state: DevicesDeviceState, path: string): boolean {
  return getPropertyValue(state, path) !== undefined;
}

/**
 * Get list of included property paths from state
 */
export function getIncludedPropertyPaths(state: DevicesDeviceState): string[] {
  const paths: string[] = [];

  for (const [type, typeState] of Object.entries(state)) {
    if (typeState && typeof typeState === 'object') {
      for (const prop of Object.keys(typeState)) {
        if ((typeState as Record<string, unknown>)[prop] !== undefined) {
          paths.push(`${type}.${prop}`);
        }
      }
    }
  }

  return paths;
}

/**
 * Get default state for device (only primary property included)
 */
export function getDefaultStateForDevice(device: DevicesDevice): DevicesDeviceState {
  const primaryProp = getPrimaryPropertyDef(device);
  if (!primaryProp) {
    // Fallback for unknown device types
    return { switch: { on: true } };
  }

  return setPropertyValue({}, primaryProp.path, primaryProp.getDefaultValue(device));
}

/**
 * Get all current device state values for properties (for "Use current values" feature)
 */
export function getCurrentValuesForDevice(device: DevicesDevice): DevicesDeviceState {
  const props = getPropertyDefsForDevice(device);
  let state: DevicesDeviceState = {};

  for (const prop of props) {
    const currentValue = getPropertyValue(device.state ?? {}, prop.path);
    if (currentValue !== undefined) {
      state = setPropertyValue(state, prop.path, currentValue);
    }
  }

  return state;
}

/**
 * Generate a summary string for display (e.g., "Power: On, Volume: 50%")
 */
export function generateStateSummary(state: DevicesDeviceState, device: DevicesDevice): string {
  const props = getPropertyDefsForDevice(device);
  const includedPaths = getIncludedPropertyPaths(state);

  const summaryParts: string[] = [];

  for (const path of includedPaths) {
    const prop = props.find((p) => p.path === path);
    if (prop) {
      const value = getPropertyValue(state, path);
      let summaryValue = prop.formatSummary(value);
      if (prop.controlType === 'select' && prop.getSelectOptions) {
        const options = prop.getSelectOptions(device);
        const match = options.find((option) => option.value === String(value ?? ''));
        if (match?.label) {
          summaryValue = match.label;
        }
      }
      summaryParts.push(`${prop.label}: ${summaryValue}`);
    }
  }

  return summaryParts.length > 0 ? summaryParts.join(' · ') : 'No properties set';
}

/**
 * Prune state by removing any empty type objects
 */
export function pruneState(state: DevicesDeviceState): DevicesDeviceState {
  const pruned: DevicesDeviceState = {};

  for (const [type, typeState] of Object.entries(state)) {
    if (typeState && typeof typeState === 'object' && Object.keys(typeState).length > 0) {
      // Also check that at least one property has a defined value
      const hasDefinedValue = Object.values(typeState).some((v) => v !== undefined);
      if (hasDefinedValue) {
        pruned[type as keyof DevicesDeviceState] = typeState as never;
      }
    }
  }

  return pruned;
}

/**
 * Check if state has at least one included property
 */
export function hasIncludedProperties(state: DevicesDeviceState): boolean {
  return getIncludedPropertyPaths(state).length > 0;
}
